import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { isAfter } from "date-fns";
import { useForm } from "react-hook-form";

//COMPONENTE
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import PartsListWithCheckbox from "@/main/components/shared/partsListWithCheckbox/partsListWithCheckbox";
import KOGFileUploader from "@/main/components/shared/fileUploader/fileUploader";
import ColumnLabel from "@/main/components/common/columnLabel/columnLabel";
import KogInputTextControlledNew from "@/main/components/shared/inputText/inputTextControlledNew";
import KogKeyboardDatePicker from "@/main/components/shared/datepicker/keyboardDatePicker";

//SERVICES/API
import { SaveProcurementInvoiceRequest } from "@/main/models/request";
import { getTranslateByKey, isNullUndefined } from "@/main/services/utils";
import { initalDateForm, validateFormatDate } from "@/main/services/utilsDate";
import { invoiceApi } from "@/main/api";

//TYPES
import { IInfoSideComponent } from "../../../cardGrid/cardGrid";
import { GetItemsByStatusResponse } from "@/main/models/response";

//STYLES
import { DateContainerDefault } from "@/main/components/shared/styles/dateStyles";

//SCREENS/COMPONENTS/STYLES LOCAL
import * as S from "./styles";

interface IInvoceForm {
	invoiceNumber: number | null;
	issueDate: Date;
}

interface IReceivedToInvoiced {
	closeModal: () => void;
	options: GetItemsByStatusResponse[];
	optionsSelected: string[];
	setOptionsSelected: React.Dispatch<React.SetStateAction<string[]>>;
	infoSideComponent: IInfoSideComponent;
	loadViewOrder: (guidQuoteProcurement: string | undefined) => Promise<void>;
	handleSubmitChangeStatus(): void;
	loadingOptions: boolean;
}

function ReceivedToInvoiced({
	closeModal,
	options,
	optionsSelected,
	infoSideComponent,
	setOptionsSelected,
	handleSubmitChangeStatus,
	loadingOptions,
}: IReceivedToInvoiced) {
	//#region State
	const [files, setFiles] = useState<File[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [changeIssueDate, setChangeIssueDate] = useState<Date | number>(0);
	const [quoteCreationDate, setQuoteCreationDate] = useState<Date | number>(0);
	const [mensageErrorIssueDate, setMensageErrorIssueDate] = useState<string | null>(null);
	//#endregion;

	//#region React Hook Form
	const INITIAL_FORM_STATE = {
		invoiceNumber: null as number | null,
		issueDate: new Date(),
	};

	const {
		control,
		handleSubmit,
		formState: { errors },
		setError,
		clearErrors,
		setValue,
		watch,
	} = useForm<IInvoceForm>({
		mode: "all",
		defaultValues: INITIAL_FORM_STATE,
	});

	const watcherIssueDate = watch("issueDate");
	//#endregion;

	function functionSubmit(dataForm: IInvoceForm) {
		if (quoteCreationDate && dataForm.issueDate) {
			if (isAfter(new Date(quoteCreationDate).getTime(), new Date(dataForm.issueDate).getTime())) {
				setError("issueDate", {
					type: "issueDateMustNotBeLessThanTheOrderDate",
				});
				setMensageErrorIssueDate(getTranslateByKey("information.issueDateMustNotBeLessThanTheOrderDate", true));
				return true;
			}
		}

		if (files.length === 0) {
			toast.error(getTranslateByKey("importInventory.pleaseSelectAtLeast1File", true));
		}

		if (infoSideComponent.guidProcurementSupplier && infoSideComponent.guidQuoteProcurement) {
			setLoading(true);
			const requestIssueDate = new Date(dataForm.issueDate).setUTCHours(0, 0, 0, 0);

			const request: SaveProcurementInvoiceRequest = {
				procurementSupplierItemGuids: optionsSelected,
				guidProcurementSupplier: infoSideComponent.guidProcurementSupplier,
				guidProcurement: infoSideComponent.guidQuoteProcurement,
				invoiceNumber: String(dataForm.invoiceNumber),
				issueDate: new Date(requestIssueDate),
				uploadFile: files,
			};
			invoiceApi
				.saveProcurementInvoice(request)
				.then((res) => {
					const resData = res.data.result;
					if (resData) {
						handleSubmitChangeStatus();
					}
				})
				.finally(() => setLoading(false));
		}
	}

	function onLoadDatePicker() {
		const dateResult = initalDateForm();
		setValue("issueDate", dateResult);
		setChangeIssueDate(new Date(dateResult).setHours(0, 0, 0, 0));
	}

	//#region useEffect
	useEffect(() => {
		onLoadDatePicker();
	}, []);

	useEffect(() => {
		if (options && options.length > 0) {
			setQuoteCreationDate(new Date(options[0].creationDate).setHours(0, 0, 0, 0));
		}
	}, [options]);
	//#endregion;

	return (
		<>
			<form onSubmit={handleSubmit(functionSubmit)} noValidate>
				<S.Content>
					<ColumnLabel
						label={getTranslateByKey("information.selectParts")}
						content={
							<PartsListWithCheckbox
								parts={options}
								optionsSelected={optionsSelected}
								setOptionsSelected={setOptionsSelected}
								isLoading={loadingOptions}
							/>
						}
					/>
					<ColumnLabel
						label={getTranslateByKey("information.invoiceData")}
						content={
							<S.BoxInput>
								<KogInputTextControlledNew
									name="invoiceNumber"
									label={getTranslateByKey("information.invoiceNumber")}
									control={control}
									controllerProps={{
										rules: { required: true, maxLength: 200 },
									}}
									errorMessageCustom={
										errors.invoiceNumber?.type === "required"
											? getTranslateByKey("information.invoiceNumberRequired", true)
											: ""
									}
									id="id-modalReceivedToInvoiced-txt-invoiceNumber"
								/>
								<DateContainerDefault>
									<KogKeyboardDatePicker
										name="issueDate"
										required
										label={getTranslateByKey("information.issueDate")}
										value={watcherIssueDate}
										isFieldInModal
										error={!!errors.issueDate || !!mensageErrorIssueDate}
										helperText={mensageErrorIssueDate}
										onChange={(date) => {
											setChangeIssueDate(new Date(date).setHours(0, 0, 0, 0));
											if (date instanceof Date) {
												setValue("issueDate", date);
											}
											if (
												quoteCreationDate &&
												date &&
												isAfter(new Date(quoteCreationDate).getTime(), new Date(date).getTime())
											) {
												setError("issueDate", {
													type: "issueDateMustNotBeLessThanTheOrderDate",
												});
												return setMensageErrorIssueDate(
													getTranslateByKey("information.issueDateMustNotBeLessThanTheOrderDate", true),
												);
											}

											if (!isNullUndefined(date)) {
												clearErrors("issueDate");
												setMensageErrorIssueDate(null);
											}
											if (isNullUndefined(date)) {
												setError("issueDate", { type: "required" });
												return setMensageErrorIssueDate(getTranslateByKey("information.issueDateRequired", true));
											}
										}}
										onBlur={() => {
											if (!validateFormatDate(changeIssueDate) && !mensageErrorIssueDate) {
												setError("issueDate", { type: "invalidDateFormat" });
												return setMensageErrorIssueDate(getTranslateByKey("information.invalidDateFormat", true));
											}
										}}
										id="id-modalReceivedToInvoiced-txt-issueDate"
									/>
								</DateContainerDefault>
							</S.BoxInput>
						}
					/>
					<ColumnLabel
						label={getTranslateByKey("information.uploadInvoices")}
						content={
							<KOGFileUploader
								isFullScreenDragDrop
								maxFileByType={{
									xml: 1,
									pdf: 1,
								}}
								files={files}
								setFiles={setFiles}
								maxFiles={2}
								typeFiles={["invoices"]}
							/>
						}
					/>
				</S.Content>

				<S.ContainerButton>
					<SecondaryButton type="button" onClick={closeModal} id="id-modalReceivedToInvoiced-btn-back">
						{getTranslateByKey("information.back")}
					</SecondaryButton>
					<SecondaryButton
						type="submit"
						disabled={optionsSelected.length == 0 || files.length == 0 || loading}
						isLoading={loading}
						id="id-modalReceivedToInvoiced-btn-invoice"
					>
						{getTranslateByKey("information.invoice")}
					</SecondaryButton>
				</S.ContainerButton>
			</form>
		</>
	);
}

export default ReceivedToInvoiced;
